// src/components/Globe/GlobePageIntegrated.jsx - VERS&Atilde;O ATUALIZADA
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import 'mapbox-gl/dist/mapbox-gl.css';
import './GlobePage.css';
import { Map as ReactMapGL, Marker, NavigationControl, FullscreenControl, Popup, Source, Layer } from 'react-map-gl';
import { useApi } from '../hooks/useApi';
import HexagonLayer from './HexagonLayer';
import SensorModal from './SensorModal';
import ImprovedRanking from './ImprovedRanking';
import UserProfile from './UserProfile';
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiHome, 
  FiWind, 
  FiHexagon, 
  FiMapPin, 
  FiSettings, 
  FiClock
} from 'react-icons/fi';

// Fun&ccedil;&atilde;o para formatar timestamp de forma amig&aacute;vel
const formatLastUpdate = (timestamp) => {
  if (!timestamp) return 'Sem dados';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `${diffMins} min atr&aacute;s`;
    if (diffHours < 24) return `${diffHours}h atr&aacute;s`;
    if (diffDays < 7) return `${diffDays}d atr&aacute;s`;
    
    // Se for mais antigo, mostrar data formatada
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  } catch (error) {
    return 'Data inv&aacute;lida';
  }
};

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYmllbHJvMjAxOCIsImEiOiJjbHhucDR2ZjkwNmo1MmxvYWgyZ3l6OHBpIn0.vB-8Vo9B4D4WWmyiXLCavw';

const GlobePageIntegrated = () => {
  const navigate = useNavigate();
  const { sensors, loading, error, fetchSensors } = useApi();
  
  // Obter email do usu&aacute;rio logado
  const [userEmail, setUserEmail] = useState(() => {
    return localStorage.getItem('user_email') || sessionStorage.getItem('user_email') || '';
  });
  
  // Fun&ccedil;&atilde;o de logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_email');
    navigate('/login');
  }, [navigate]);

  const [viewState, setViewState] = useState({
    longitude: -47.89306978980245,
    latitude: -15.792926726089211,
    zoom: 2.5,
    pitch: 0,
    bearing: 0
  });

  const [selectedSensor, setSelectedSensor] = useState(null);
  const [selectedHexagon, setSelectedHexagon] = useState(null);
  const [activeLayer, setActiveLayer] = useState('indoor');
  const [filter, setFilter] = useState('all');
  const [isDark, setIsDark] = useState(true);
  const [isLayerControlsCollapsed, setIsLayerControlsCollapsed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRankingMobileOpen, setIsRankingMobileOpen] = useState(false);
  const [isControlsMobileOpen, setIsControlsMobileOpen] = useState(false);
  const [useHexagons, setUseHexagons] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Efeito inicial
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const dark = savedTheme === 'dark';
    setIsDark(dark);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Detectar mudan&ccedil;as no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsRankingMobileOpen(false);
        setIsControlsMobileOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Buscar dados com tipo correto
  useEffect(() => {
    fetchSensors(activeLayer);
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(() => {
      fetchSensors(activeLayer);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchSensors, activeLayer]);

  useEffect(() => {
    if (sensors.length > 0) {
      console.log('📍 Sensores no mapa:', sensors);
      console.log('📊 Primeiro sensor:', sensors[0]);
    }
  }, [sensors]);

  // Toggle theme otimizado
  const toggleTheme = useCallback(() => {
    setSelectedSensor(null);
    setSelectedHexagon(null); // NOVO
    const newDark = !isDark;
    setIsDark(newDark);
    const newTheme = newDark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  }, [isDark]);

  // Cor do marker otimizada
  const getMarkerColor = useCallback((sensor) => {
    if (activeLayer === 'airQuality') {
      const aqi = sensor.aqi;
      if (aqi < 50) return '#00ff88';
      if (aqi < 100) return '#ffeb3b';
      if (aqi < 150) return '#ff9800';
      if (aqi < 200) return '#f44336';
      return '#9c27b0';
    } else {
      const quality = sensor.quality;
      if (quality > 80) return '#00ff88';
      if (quality > 60) return '#00d4ff';
      if (quality > 40) return '#ffeb3b';
      if (quality > 20) return '#ff9800';
      return '#f44336';
    }
  }, [activeLayer]);

  // Sensores filtrados memoizados
  const filteredSensors = useMemo(() => {
    return sensors.filter(sensor => {
      if (filter === 'all') return true;
      if (filter === 'best') {
        return activeLayer === 'airQuality' ? sensor.aqi < 50 : sensor.quality > 80;
      }
      if (filter === 'worst') {
        return activeLayer === 'airQuality' ? sensor.aqi > 150 : sensor.quality < 40;
      }
      return true;
    });
  }, [sensors, filter, activeLayer]);

  // Sensores vis&iacute;veis memoizados
  const visibleSensors = useMemo(() => {
    return filteredSensors;
  }, [filteredSensors]);

  // Obter dados dos hex&aacute;gonos com debounce para suavizar transi&ccedil;&otilde;es
  const { hexagonData, h3Resolution } = useMemo(() => {
    if (!useHexagons) return { hexagonData: [], h3Resolution: 0 };
    
    // Usar zoom arredondado para reduzir rec&aacute;lculos frequentes
    const stableZoom = Math.round(viewState.zoom * 2) / 2;
    
    return HexagonLayer({
      sensors: visibleSensors,
      zoom: stableZoom,
      activeLayer,
      getMarkerColor
    });
  }, [visibleSensors, Math.round(viewState.zoom * 2) / 2, activeLayer, getMarkerColor, useHexagons]);

  // Estilo do mapa memoizado
  const currentMapStyle = useMemo(() => {
    return isDark 
      ? 'mapbox://styles/mapbox/dark-v11' 
      : 'mapbox://styles/mapbox/light-v11';
  }, [isDark]);

  // Atualizar mudan&ccedil;a de layer
  const handleLayerChange = useCallback((newLayer) => {
    setActiveLayer(newLayer);
    setSelectedSensor(null);
    setSelectedHexagon(null);
  }, []);

  // Handler para clique no sensor do ranking
  const handleRankingSensorClick = useCallback((sensor) => {
    setSelectedSensor(sensor);
    setSelectedHexagon(null);
    setViewState({
      ...viewState,
      longitude: sensor.lng || sensor.longitude,
      latitude: sensor.lat || sensor.latitude,
      zoom: 8,
      transitionDuration: 1000
    });
  }, [viewState]);

  // Renderizar hex&aacute;gonos
  const renderHexagons = () => {
    const visibleHexagons = hexagonData.filter(hex => hex.sensors.length > 0);
    
    return visibleHexagons.map((hex) => (
      <Source
        key={`${hex.hexId}-${h3Resolution}`}
        id={`hex-${hex.hexId}`}
        type="geojson"
        data={{
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [hex.boundary.map(([lat, lng]) => [lng, lat])]
          },
          properties: {
            color: hex.color,
            count: hex.sensors.length,
            resolution: h3Resolution
          }
        }}
      >
        <Layer
          id={`hex-fill-${hex.hexId}`}
          type="fill"
          paint={{
            'fill-color': hex.color,
            'fill-opacity': viewState.zoom > 6 ? 0.4 : 0.3,
            'fill-outline-color': hex.color
          }}
          onClick={() => setSelectedHexagon(hex)}
        />
        <Layer
          id={`hex-line-${hex.hexId}`}
          type="line"
          paint={{
            'line-color': hex.color,
            'line-width': viewState.zoom > 8 ? 1.5 : 1,
            'line-opacity': 0.6
          }}
        />
        {hex.sensors.length > 1 && viewState.zoom > 10 && (
          <Layer
            id={`hex-label-${hex.hexId}`}
            type="symbol"
            layout={{
              'text-field': hex.sensors.length.toString(),
              'text-size': Math.min(12, viewState.zoom),
              'text-anchor': 'center',
              'text-allow-overlap': false
            }}
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': hex.color,
              'text-halo-width': 1.5
            }}
          />
        )}
      </Source>
    ));
  };

  // Renderizar marcadores (quando n&atilde;o usar hex&aacute;gonos)
  const renderMarkers = () => {
    return visibleSensors.map((sensor) => (
      <Marker
        key={sensor.id}
        longitude={sensor.lng || sensor.longitude}
        latitude={sensor.lat || sensor.latitude}
        anchor="center"
        onClick={e => {
          e.originalEvent.stopPropagation();
          setSelectedSensor(sensor);
          setSelectedHexagon(null);
        }}
      >
        <div
          className="custom-marker-optimized"
          style={{
            backgroundColor: getMarkerColor(sensor),
            width: viewState.zoom > 6 ? '14px' : '10px',
            height: viewState.zoom > 6 ? '14px' : '10px',
            borderRadius: '50%',
            border: '2px solid rgba(255, 255, 255, 0.8)',
            cursor: 'pointer',
            boxShadow: `0 0 10px ${getMarkerColor(sensor)}`,
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        />
      </Marker>
    ));
  };

  return (
    <div className="globe-container">
      {/* Loading State */}
      {loading && (
        <div className="status-indicator loading">
          Carregando dados...
        </div>
      )}


      {/* Error State */}
      {error && (
        <div className="status-indicator error">
          ❌ Erro: {error}
        </div>
      )}

      {/* Header */}
      <div className="globe-header">
        <div className="header-logo-section">
          <img 
            src="/assets/images/uber-logo.png" 
            alt="Uber" 
            className={`uber-logo-header ${isDark ? 'logo-white' : 'logo-black'}`}
          />
          <span className="logo-text">STATS Protocol</span>
        </div>
        
        <div className="header-actions">
          {/* Toggle Hex&aacute;gonos/Marcadores */}
          <div className="view-toggle">
            <button
              className={`toggle-btn ${useHexagons ? 'active' : ''}`}
              onClick={() => setUseHexagons(true)}
              title="Visualiza&ccedil;&atilde;o em Hex&aacute;gonos"
            >
              <FiHexagon size={18} />
            </button>
            <button
              className={`toggle-btn ${!useHexagons ? 'active' : ''}`}
              onClick={() => setUseHexagons(false)}
              title="Visualiza&ccedil;&atilde;o em Marcadores"
            >
              <FiMapPin size={18} />
            </button>
          </div>
          
          <button className="theme-toggle-btn" onClick={toggleTheme}>
            {isDark ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          
          {/* Novo componente UserProfile */}
          {userEmail && (
            <UserProfile 
              userEmail={userEmail} 
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>

      {/* Map */}
      <ReactMapGL
        key={`map-${isDark ? 'dark' : 'light'}`}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle={currentMapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={useHexagons ? hexagonData.map(h => `hex-fill-${h.hexId}`) : []}
        onClick={(event) => {
          if (useHexagons && event.features?.length > 0) {
            const hexId = event.features[0].layer.id.replace('hex-fill-', '');
            const hex = hexagonData.find(h => h.hexId === hexId);
            if (hex) {
              setSelectedHexagon(hex);
              setSelectedSensor(null); // Limpar sensor selecionado
            }
          }
        }}
        fog={{}}
      >
        <NavigationControl position="top-right" style={{ marginTop: '80px' }} />
        <FullscreenControl position="top-right" style={{ marginTop: '120px' }} />

        {/* RENDERIZA&Ccedil;&Atilde;O CONDICIONAL: Hex&aacute;gonos ou Marcadores */}
        {useHexagons && hexagonData.length > 0 ? renderHexagons() : null}
        {!useHexagons ? renderMarkers() : null}
      </ReactMapGL>

      {/* Modal Unificado para Hex&aacute;gonos e Marcadores */}
      <SensorModal
        sensor={selectedSensor}
        hexData={selectedHexagon}
        activeLayer={activeLayer}
        onClose={() => {
          setSelectedSensor(null);
          setSelectedHexagon(null);
        }}
        getMarkerColor={getMarkerColor}
      />

      {/* Info de performance */}
      <div className="performance-info">
        <span>API: {sensors.length} devices</span>
        <span>Showing: {visibleSensors.length}</span>
        <span>View: {useHexagons ? `Hexagons (R${h3Resolution})` : 'Markers'}</span>
        <span>Layer: {activeLayer}</span>
      </div>

      {/* Layer Controls */}
      <motion.div 
        className={`layer-controls ${isLayerControlsCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile-hidden' : ''} ${isControlsMobileOpen ? 'mobile-open' : ''}`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <div className="panel-header">
          <h3>
            {isLayerControlsCollapsed ? (
              <>
                <FiSettings size={18} />
                <span>Controls</span>
              </>
            ) : (
              'Controls'
            )}
          </h3>
          <button 
            className="globe-collapse-btn" 
            onClick={() => {
              if (isMobile) {
                setIsControlsMobileOpen(false);
              } else {
                setIsLayerControlsCollapsed(!isLayerControlsCollapsed);
              }
            }}
            aria-label={isLayerControlsCollapsed ? "Expandir controles" : "Retrair controles"}
          >
            {isMobile ? (
              <FiChevronLeft size={16} />
            ) : (
              isLayerControlsCollapsed ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />
            )}
          </button>
        </div>

        {(!isLayerControlsCollapsed || isControlsMobileOpen) && (
          <>
            <div className="control-group">
              <h4>Data Layers</h4>
              <div className="layer-buttons">
                <button
                  className={activeLayer === 'indoor' ? 'active' : ''}
                  onClick={() => handleLayerChange('indoor')}
                >
                  <FiHome size={18} />
                  <span>Indoor Sensors</span>
                </button>
                <button
                  className={activeLayer === 'airQuality' ? 'active' : ''}
                  onClick={() => handleLayerChange('airQuality')}
                >
                  <FiWind size={18} />
                  <span>Outdoor Air Quality</span>
                </button>
              </div>
            </div>

            <div className="legend">
              <h4>Legend</h4>
              {activeLayer === 'airQuality' ? (
                <div className="legend-items">
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#00ff88' }}></span>
                    <span>Good (0-50)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#ffeb3b' }}></span>
                    <span>Moderate (51-100)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#ff9800' }}></span>
                    <span>Unhealthy (101-150)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#f44336' }}></span>
                    <span>Very Unhealthy (151-200)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#9c27b0' }}></span>
                    <span>Hazardous (200+)</span>
                  </div>
                </div>
              ) : (
                <div className="legend-items">
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#00ff88' }}></span>
                    <span>Excellent (80-100)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#00d4ff' }}></span>
                    <span>Good (60-80)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#ffeb3b' }}></span>
                    <span>Fair (40-60)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#ff9800' }}></span>
                    <span>Poor (20-40)</span>
                  </div>
                  <div className="legend-item">
                    <span className="color-box" style={{ background: '#f44336' }}></span>
                    <span>Bad (0-20)</span>
                  </div>
                </div>
              )}
            </div>

          </>
        )}
      </motion.div>

      {/* Overlay escuro para mobile - permite fechar clicando no globo */}
      {isMobile && (isRankingMobileOpen || isControlsMobileOpen) && (
        <div 
          className="mobile-overlay mobile-open"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsRankingMobileOpen(false);
              setIsControlsMobileOpen(false);
            }
          }}
        />
      )}

      {/* Bot&atilde;o flutuante para abrir ranking em mobile */}
      {isMobile && !isRankingMobileOpen && (
        <button
          className="mobile-floating-btn mobile-ranking-btn"
          onClick={() => {
            setIsRankingMobileOpen(true);
            setIsControlsMobileOpen(false);
          }}
          title="Abrir Rankings"
        >
          <FiHome size={20} />
        </button>
      )}

      {/* Bot&atilde;o flutuante para abrir controles em mobile */}
      {isMobile && !isControlsMobileOpen && (
        <button
          className="mobile-floating-btn mobile-controls-btn"
          onClick={() => {
            setIsControlsMobileOpen(true);
            setIsRankingMobileOpen(false);
          }}
          title="Abrir Controles"
        >
          <FiSettings size={20} />
        </button>
      )}

      {/* SUBSTITUIR Sidebar com Ranking MELHORADO */}
      <motion.div 
        className={`globe-ranking-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${isRankingMobileOpen ? 'mobile-open' : ''}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <div className="globe-ranking-header">
          <div className="globe-ranking-title">
            <h2>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {activeLayer === 'airQuality' ? (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                    Outdoor Air Quality
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3 7h7l-6 4 2 7-6-4-6 4 2-7-6-4h7z"/>
                    </svg>
                    Indoor
                  </>
                )}
                {' '}Rankings
              </span>
            </h2>
            <button 
              className="globe-collapse-btn" 
              onClick={() => {
                if (isMobile) {
                  setIsRankingMobileOpen(false);
                } else {
                  setIsSidebarCollapsed(!isSidebarCollapsed);
                }
              }}
            >
              {isMobile ? (
                <FiChevronLeft size={16} />
              ) : (
                isSidebarCollapsed ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        {(!isSidebarCollapsed || isRankingMobileOpen) && (
          <>
            {/* USAR O COMPONENTE ImprovedRanking */}
            <ImprovedRanking
              sensors={visibleSensors}
              activeLayer={activeLayer}
              onSensorClick={handleRankingSensorClick}
            />

            <div className="globe-ranking-footer">
              <div className="globe-stats-summary">
                <div className="globe-summary-item">
                  <span className="label">Total Devices</span>
                  <span className="value">{sensors.length}</span>
                </div>
                <div className="globe-summary-item">
                  <span className="label">Active Layer</span>
                  <span className="value">{activeLayer === 'airQuality' ? 'Outdoor Air Quality' : 'Indoor'}</span>
                </div>
                <div className="globe-summary-item">
                  <span className="label">View Mode</span>
                  <span className="value">{useHexagons ? 'Hexagons' : 'Markers'}</span>
                </div>
              </div>
              {(() => {
                const lastUpdates = sensors
                  .map(s => s.lastUpdate)
                  .filter(Boolean)
                  .sort((a, b) => new Date(b) - new Date(a));
                
                const mostRecentUpdate = lastUpdates[0];
                
                return mostRecentUpdate ? (
                  <div style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border-color)',
                    fontSize: '11px',
                    color: '#999',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    <FiClock size={12} />
                    <span>&Uacute;ltima atualiza&ccedil;&atilde;o: {formatLastUpdate(mostRecentUpdate)}</span>
                  </div>
                ) : null;
              })()}
            </div>
          </>
        )}
      </motion.div>
      
      {/* Footer Powered by */}
      <div className="globe-footer">
        <p>Powered by <strong>STATS Protocol</strong></p>
      </div>
    </div>
  );
};

export default GlobePageIntegrated;